import { DataTable } from 'mantine-datatable';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  ActionIcon,
  Button,
  Divider,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconClipboardHeart,
  IconPencil,
  IconPill,
  IconPlus,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { dateToDateString, dateToHourString } from '@utils/date';
import { mapWeekDay } from '@utils/mappers';

import { Therapy, TherapyDrug } from '@interfaces/therapies.interface';

import { isApiError } from '@api/index';
import {
  useDeleteTherapyDrugMutation,
  useDeleteTherapyMutation,
  useGetTherapyByIdQuery,
} from '@api/therapies.api';

import useLayoutProps from '@hooks/useLayoutProps';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import UpsertTherapyDrugForm, {
  UPSERT_THERAPY_DRUG_FORM_MODAL_ID,
} from '@components/therapies/UpsertTherapyDrugForm';
import UpsertTherapyForm, {
  UPSERT_THERAPY_FORM_MODAL_ID,
} from '@components/therapies/UpsertTherapyForm';

import classes from './TherapyDetail.module.css';

export default function TherapyDetail() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { therapyId } = useParams();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');

  const navigate = useNavigate();

  const canUpdateTherapy = useUserPermissionGuard('THERAPIES.UPDATE');
  const canDeleteTherapy = useUserPermissionGuard('THERAPIES.DELETE');

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: therapy, isLoading: isLoadingTherapy } = useGetTherapyByIdQuery(
    therapyId!,
  );

  const [deleteTherapy, { isLoading: isLoadingDeleteTherapy }] =
    useDeleteTherapyMutation();

  const [deleteTherapyDrug, { isLoading: isLoadingDeleteTherapyDrug }] =
    useDeleteTherapyDrugMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleEditTherapyClick = useCallback((therapy: Therapy) => {
    openModal({
      modalId: UPSERT_THERAPY_FORM_MODAL_ID,
      title: 'Modifica terapia',
      children: (
        <UpsertTherapyForm therapy={therapy} patientId={therapy.patientId} />
      ),
    });
  }, []);

  const handleDeleteTherapyClick = useCallback((id: string) => {
    openConfirmModal({
      title: 'Elimina terapia',
      children:
        "Stai per eliminare la terapia. L'operazione non è reversibile.\nContinuare?",
      confirmProps: { color: 'red' },
      labels: { confirm: 'Conferma', cancel: 'Annulla' },
      onConfirm: async () => {
        try {
          await deleteTherapy(id).unwrap();

          showNotification({
            title: 'Terapia eliminata',
            message: 'Terapia eliminata con successo',
          });

          navigate(-1);
        } catch (e) {
          if (isApiError(e)) {
            showNotification({
              title: 'Errore',
              message: e.data.message,
              color: 'red',
              icon: <IconX />,
            });
          } else {
            showNotification({
              title: 'Errore',
              message: 'Errore sconosciuto',
              color: 'red',
              icon: <IconX />,
            });
            console.error(e);
          }
        }
      },
    });
  }, []);

  const handleAddTherapyDrugClick = useCallback(() => {
    openModal({
      modalId: UPSERT_THERAPY_DRUG_FORM_MODAL_ID,
      title: 'Aggiungi farmaco',
      children: <UpsertTherapyDrugForm therapyId={therapyId!} />,
    });
  }, []);

  const handleEditTherapyDrugClick = useCallback((drug: TherapyDrug) => {
    openModal({
      modalId: UPSERT_THERAPY_DRUG_FORM_MODAL_ID,
      title: `Posologia ${drug.drug.name}`,
      children: (
        <UpsertTherapyDrugForm therapyId={therapyId!} therapyDrug={drug} />
      ),
    });
  }, []);

  const handleDeleteTherapyDrugClick = useCallback((aic: string) => {
    openConfirmModal({
      title: 'Rimuovi farmaco',
      children:
        "Stai per rimuovere il farmaco dalla terapia. L'operazione non è reversibile.\nContinuare?",
      confirmProps: { color: 'red' },
      labels: { confirm: 'Conferma', cancel: 'Annulla' },
      onConfirm: async () => {
        try {
          await deleteTherapyDrug({
            therapyId: therapyId!,
            drugAic: aic,
          }).unwrap();

          showNotification({
            title: 'Farmaco rimosso',
            message: 'Farmaco rimosso con successo',
          });
        } catch (e) {
          if (isApiError(e)) {
            showNotification({
              title: 'Errore',
              message: e.data.message,
              color: 'red',
              icon: <IconX />,
            });
          } else {
            showNotification({
              title: 'Errore',
              message: 'Errore sconosciuto',
              color: 'red',
              icon: <IconX />,
            });
            console.error(e);
          }
        }
      },
    });
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => (
      <Group>
        <IconClipboardHeart />
        {therapy ? `${therapy.description}` : ''}
      </Group>
    ),
    [therapy],
  );

  const backLink = useMemo(
    () => ({
      title: patientId ? 'Paziente' : 'Terapie',
      to: patientId ? `/pazienti/${patientId}` : '/terapie',
    }),
    [patientId],
  );

  useLayoutProps({
    title,
    backLink,
  });

  return isLoadingTherapy ? (
    <Loader />
  ) : !therapy ? (
    <Text fs="italic">Terapia non trovata o non accessibile</Text>
  ) : (
    <>
      <Paper withBorder p="md" mb="xl">
        <Group justify="space-between" align="start">
          <div>
            <Title order={4} mb="md">
              Informazioni terapia
            </Title>
            <Text>
              <Text fw={700} span>
                Descrizione:
              </Text>{' '}
              {therapy.description}
            </Text>
            <Text>
              <Text fw={700} span>
                Data inizio:
              </Text>{' '}
              {dateToDateString(new Date(therapy.startDate))}
            </Text>
            <Text>
              <Text fw={700} span>
                Data fine:
              </Text>{' '}
              {therapy.endDate
                ? dateToDateString(new Date(therapy.endDate))
                : '-'}
            </Text>
          </div>

          <Stack>
            {canUpdateTherapy && (
              <Button
                leftSection={<IconPencil />}
                variant="outline"
                onClick={() => handleEditTherapyClick(therapy)}
              >
                Modifica
              </Button>
            )}
            {canDeleteTherapy && (
              <Button
                leftSection={<IconTrash />}
                color="red"
                onClick={() => handleDeleteTherapyClick(therapy.id)}
                loading={isLoadingDeleteTherapy}
              >
                Elimina
              </Button>
            )}
          </Stack>
        </Group>
      </Paper>

      <Group justify="space-between" align="center" mb="lg">
        <Group>
          <IconPill />
          <Title order={3}>Farmaci terapia</Title>
        </Group>

        {canUpdateTherapy && (
          <Button
            leftSection={<IconPlus />}
            onClick={handleAddTherapyDrugClick}
          >
            Aggiungi farmaco
          </Button>
        )}
      </Group>

      {therapy.drugs.length === 0 ? (
        <Text fs="italic">Nessun farmaco associato a questa terapia</Text>
      ) : (
        <>
          <DataTable
            /* General */
            striped
            /* Data */
            idAccessor="drug.aic"
            columns={[
              {
                accessor: 'drug.aic',
                title: 'AIC',
                width: '0%',
              },
              {
                accessor: 'drug.aicNameHolder',
                title: 'Titolare AIC',
              },
              {
                accessor: 'drug.name',
                title: 'Nome',
              },
              {
                accessor: 'drug.activeIngredient',
                title: 'Principio attivo',
              },
              {
                accessor: 'drugMeasureUnit',
                title: 'Unità di misura',
              },
              {
                accessor: 'actions',
                title: '',
                textAlign: 'right',
                width: '0%',
                render: (record) => (
                  <Group wrap="nowrap" gap="xs">
                    <Tooltip label="Modifica posologia">
                      <ActionIcon
                        variant="subtle"
                        onClick={() => handleEditTherapyDrugClick(record)}
                      >
                        <IconPencil />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Rimuovi farmaco">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        disabled={isLoadingDeleteTherapyDrug}
                        onClick={() =>
                          handleDeleteTherapyDrugClick(record.drug.aic)
                        }
                      >
                        <IconTrash />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                ),
              },
            ]}
            records={therapy.drugs}
          />

          <Title order={4} mt="xl">
            Posologia
          </Title>

          <ScrollArea mt="md" type="auto" offsetScrollbars>
            <Group
              className={classes.posologyContainer}
              grow
              align="start"
              gap="xl"
              wrap="nowrap"
            >
              {Array.from({ length: 7 }).map((_, index) => {
                const drugs = therapy.drugs
                  .filter((drug) =>
                    drug.posology.some(
                      (posology) => posology.dayOfWeek === index + 1,
                    ),
                  )
                  .map((drug) => ({
                    ...drug,
                    posology: drug.posology.filter(
                      (posology) => posology.dayOfWeek === index + 1,
                    ),
                  }))
                  .reduce(
                    (acc, drug) => {
                      for (const p of drug.posology) {
                        if (!acc[p.hour]) {
                          acc[p.hour] = [];
                        }

                        acc[p.hour].push({ ...drug, posology: [p] });
                      }

                      return acc;
                    },
                    {} as Record<string, TherapyDrug[]>,
                  );

                return (
                  <Stack key={index} miw="20rem" w="100%" maw="auto">
                    <Text fw={600}>{mapWeekDay(index + 1)}</Text>
                    {Object.keys(drugs).length === 0 ? (
                      <Text fs="italic" c="brand" size="sm" mb="lg">
                        Nessuna somministrazione
                      </Text>
                    ) : (
                      Object.entries(drugs)
                        .toSorted(
                          ([hourA], [hourB]) =>
                            new Date(hourA).getTime() -
                            new Date(hourB).getTime(),
                        )
                        .map(([hour, drugs]) => (
                          <div key={hour}>
                            <Divider
                              label={dateToHourString(new Date(hour))}
                              labelPosition="left"
                              styles={{
                                label: {
                                  color: 'var(--mantine-color-brand-6)',
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                },
                              }}
                            />

                            <Table>
                              {/* <Table.Thead>
                              <Table.Tr>
                                <Table.Th>Farmaco</Table.Th>
                                <Table.Th>Qt.</Table.Th>
                              </Table.Tr>
                            </Table.Thead> */}
                              <Table.Tbody>
                                {drugs.map((drug) => (
                                  <Table.Tr key={drug.drug.aic}>
                                    <Table.Td>{drug.drug.name}</Table.Td>
                                    <Table.Td
                                      style={{
                                        textAlign: 'right',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {drug.posology[0].quantity}{' '}
                                      {drug.drugMeasureUnit}
                                    </Table.Td>
                                  </Table.Tr>
                                ))}
                              </Table.Tbody>
                            </Table>
                          </div>
                        ))
                    )}
                  </Stack>
                );
              })}
            </Group>
          </ScrollArea>
        </>
      )}
    </>
  );
}
